import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Brand architecture per the guidelines: one masterbrand, two collections.
// "Tarashi Homes" is retired and must never appear.
export const COLLECTIONS = {
  homestays: {
    id: 'homestays',
    name: 'Tarashi Homestays',
    short: 'Homestays',
    blurb: 'Apartments and homestays, handpicked across Goa.',
    deep: '#1B4332',
    deepName: 'Konkan Green',
    article: 'a Tarashi Homestay',
  },
  villas: {
    id: 'villas',
    name: 'Tarashi Villas',
    short: 'Villas',
    blurb: 'Private villas, run to hotel standards.',
    deep: '#1F2E4E',
    deepName: 'Azulejo Indigo',
    article: 'a Tarashi Villa',
  },
};

// The API still keys the Homestays collection as `homestay` (singular) on the
// property document; the brand name is plural.
export const API_BRAND = { homestays: 'homestay', villas: 'villas' };

const BrandContext = createContext(null);

export const BrandProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const brandId = COLLECTIONS[searchParams.get('brand')] ? searchParams.get('brand') : 'homestays';

  const setBrand = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      params.set('brand', next);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const value = useMemo(
    () => ({
      brandId,
      brand: COLLECTIONS[brandId],
      apiBrand: API_BRAND[brandId],
      setBrand,
      collections: Object.values(COLLECTIONS),
    }),
    [brandId, setBrand]
  );

  return (
    <BrandContext.Provider value={value}>
      <div data-tarashi-brand={brandId} className="tarashi-site" data-testid="tarashi-site">
        {children}
      </div>
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within a BrandProvider');
  return ctx;
};
